import { useClassPerfLeaderboard } from '../../../api/hooks';
import type { ClassPerfLeaderboardSection } from '../../../api/types';
import { Button, PanelCard, SortableTh, Table, Td, TextLink, Term } from '../../../components/ui';
import Sparkline from '../../../components/charts/Sparkline';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import EmptyState from '../../../components/ui/EmptyState';
import { FaClipboardList } from '../../../components/ui/icons';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { SubRow, dayMonth } from '../lib/cpBits';
import { TH_RULE } from '../../../lib/tableRules';

interface Props {
  gradeId: number;
  /** "Grade 10", for the hint under the title. */
  gradeName?: string;
  /** The section picked in the page filters; its row is highlighted. */
  sectionId?: number | 'all';
  subjectId: number | 'all';
  weeks?: number;
  /** Offered by the empty state's "Rank by ..." button. */
  suggestSubject?: { id: number; name: string };
  onPickSubject?: (id: number) => void;
  /** Homeroom teacher names by section id. */
  homerooms?: Record<number, string>;
}

const WEAK_BELOW = 55;

const LEADERBOARD_COLUMNS: SortColumn<ClassPerfLeaderboardSection>[] = [
  { key: 'rank', accessor: (s) => s.rank, type: 'number' },
  { key: 'section', accessor: (s) => s.display },
  { key: 'avg', accessor: (s) => s.avg_score, type: 'number' },
  { key: 'students', accessor: (s) => s.students_with_data, type: 'number' },
];

export default function LeaderboardTab({ gradeId, gradeName, sectionId, subjectId, weeks, suggestSubject, onPickSubject, homerooms }: Props) {
  const q = useClassPerfLeaderboard({ grade: gradeId, subject: subjectId, weeks });
  const { rows, sort, toggle } = useSortableTable(q.data?.sections, LEADERBOARD_COLUMNS, { key: 'rank', dir: 'asc' });
  if (subjectId === 'all') {
    return (
      <PanelCard>
        <EmptyState icon={<FaClipboardList />} title="Pick a subject"
          body="Sections are ranked one subject at a time. Choose one above."
          action={suggestSubject && onPickSubject
            ? <Button variant="ghost" onClick={() => onPickSubject(suggestSubject.id)}>{`Rank by ${suggestSubject.name}`}</Button>
            : undefined} />
      </PanelCard>
    );
  }
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  const d = q.data;
  if (d.sections.length === 0) {
    return (
      <PanelCard>
        <EmptyState
          icon={<FaClipboardList />}
          title="No sections to rank"
          body="No section has data for this subject yet."
        />
      </PanelCard>
    );
  }
  const hint = [gradeName, d.week_start ? `week of ${dayMonth(d.week_start)}` : null].filter(Boolean).join(' · ');
  return (
    <PanelCard title={`Sections ranked · ${d.subject?.name ?? ''}`} help="tab_leaderboard" align="end" subtitle={hint || undefined}>
      <Table minWidth={640}>
        <thead>
          <tr>
            <SortableTh columnKey="rank" sort={sort} onToggle={toggle} help="rank" className={TH_RULE}>Rank</SortableTh>
            <SortableTh columnKey="section" sort={sort} onToggle={toggle} className={TH_RULE}>Section</SortableTh>
            <SortableTh columnKey="avg" sort={sort} onToggle={toggle} help="avg_score" className={`${TH_RULE} w-[34%]`}>Average</SortableTh>
            <SortableTh columnKey="students" sort={sort} onToggle={toggle} align="right" help="students_with_data" className={TH_RULE}>Students with data</SortableTh>
            <th className={`px-2.5 pb-2.5 text-left font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted ${TH_RULE}`}><Term k="trend_sparkline">Trend</Term></th>
            <th className={`px-2.5 pb-2.5 ${TH_RULE}`}><span className="sr-only">Section page</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className={s.id === sectionId ? '[&>td]:bg-accent-bg/50' : ''}>
              <Td>
                <span className={`grid h-[30px] w-[30px] place-items-center rounded-[10px] font-mono text-[13px] font-semibold ${
                  s.rank === 1 ? 'bg-navy text-white' : 'bg-track text-muted-2'}`}>{s.rank ?? '—'}</span>
              </Td>
              <Td>
                <span className="block font-semibold">{s.display}</span>
                {homerooms?.[s.id] && <span className="text-xs text-muted">Homeroom {homerooms[s.id]}</span>}
              </Td>
              <Td><SubRow bold value={s.avg_score} lo={s.avg_score != null && s.avg_score < WEAK_BELOW} label={`${s.display} average`} /></Td>
              <Td align="right" className="font-mono font-medium tabular-nums">{s.students_with_data}</Td>
              <Td>
                {s.trend.length > 0
                  ? <Sparkline width={96} values={s.trend.map((t) => t.avg_score)} />
                  : '—'}
              </Td>
              <Td align="right" className="whitespace-nowrap">
                <TextLink to={`/school/section/${s.id}`} aria-label={`Open ${s.display}`}>Open →</TextLink>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </PanelCard>
  );
}
